import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { EMBED_URL_CACHE_MS, EMBED_URL_QUERY_KEY } from '~/constants';
import { useApi } from '~/providers/ApiProvider';
import { buildDashboardUrl, getSessionBaseUrl, hasAuthParams, stripAuthParams } from '~/utils/embedUrlUtils';

/**
 * Fetches and caches QuickSight session base URLs.
 *
 * On first load (cache miss):
 * - Returns the full URL from API (with auth params) for initial embed
 * - After successful embed, caches just the session base URL
 *
 * On subsequent loads (cache hit):
 * - Builds URLs from cached session base URL (no auth params needed during 30 min session)
 * @param dashboardId - Dashboard ID to specify which dashboard to embed.
 * @returns sessionBaseUrl, getEmbedUrl(), isLoading, error, onEmbedSuccess()
 */
export const useEmbedUrl = (dashboardId: string | undefined) => {
  const { warehouseApi } = useApi();
  const queryClient = useQueryClient();
  const { mutateAsync } = warehouseApi.createEmbeddedDashboardUrl.useMutation();

  const query = useQuery({
    queryKey: EMBED_URL_QUERY_KEY,
    queryFn: async () => {
      const response = await mutateAsync({
        body: { data: { defaultDashboardId: dashboardId } },
      });

      const url = response.body?.data?.embedUrl;
      if (!url) {
        throw new Error('No embed URL returned from API');
      }

      return url;
    },
    staleTime: EMBED_URL_CACHE_MS,
    gcTime: EMBED_URL_CACHE_MS,
    enabled: !!dashboardId,
    retry: false,
  });

  /** Builds a full dashboard embed URL for the target dashboard. */
  const getEmbedUrl = useCallback(
    (targetDashboardId: string): string | undefined => {
      if (!query.data) {
        return undefined;
      }

      const url = query.data;

      // Fresh embed URL - use as-is (has auth params for initial SDK handshake)
      if (hasAuthParams(url)) {
        return url;
      }

      // Refetching - wait for fresh data
      if (query.isFetching) {
        return undefined;
      }

      // Session base URL - build full URL for target dashboard
      return buildDashboardUrl(url, targetDashboardId);
    },
    [query.data, query.isFetching],
  );

  /** Caches the session base URL after successful embed. */
  const onEmbedSuccess = useCallback(
    (url: string) => {
      const strippedUrl = stripAuthParams(url);
      const sessionBaseUrl = getSessionBaseUrl(strippedUrl);

      queryClient.setQueryData(EMBED_URL_QUERY_KEY, sessionBaseUrl);
    },
    [queryClient],
  );

  /** Removes cached embed URL so the next attempt fetches a fresh one from the API. */
  const invalidateSession = useCallback(() => {
    queryClient.removeQueries({ queryKey: EMBED_URL_QUERY_KEY });
  }, [queryClient]);

  /** Show loading only when actively fetching */
  const isRefetching = !!(query.data && !hasAuthParams(query.data) && query.isFetching);
  const isLoading = query.isLoading || isRefetching;

  return {
    sessionBaseUrl: query.data,
    getEmbedUrl,
    isLoading,
    error: query.error,
    onEmbedSuccess,
    invalidateSession,
  };
};
